/**
 * Pure helpers for users chain identity records (addressType 1=EOA, 2=Privy).
 */

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

export function addressesEqual(a, b) {
  const left = normalizeAddress(a);
  const right = normalizeAddress(b);
  if (!left || !right) return false;
  return left.toLowerCase() === right.toLowerCase();
}

export function getTradeAddressType(record) {
  const n = Number(record?.addressType ?? record?.type);
  return Number.isFinite(n) ? n : NaN;
}

export function hasCustodialTradeAddress(records) {
  if (!Array.isArray(records) || !records.length) return false;
  return records.some((item) => getTradeAddressType(item) === 2);
}

export function pickCustodialTradeAddressRecord(records) {
  if (!Array.isArray(records)) return null;
  return records.find((item) => getTradeAddressType(item) === 2) ?? null;
}

/**
 * @returns {'missing'|'matching'|'conflict'}
 * conflict 表示后台已有另一托管资金身份，前端不得用当前 signer 覆盖。
 */
export function resolveCustodialIdentityStatus(records, currentAddress) {
  const custodialRecords = Array.isArray(records)
    ? records.filter((item) => getTradeAddressType(item) === 2)
    : [];
  if (!custodialRecords.length) return 'missing';
  return custodialRecords.some((item) => addressesEqual(item?.address, currentAddress))
    ? 'matching'
    : 'conflict';
}

export function hasSelfCustodyTradeAddress(records) {
  if (!Array.isArray(records) || !records.length) return false;
  return records.some((item) => getTradeAddressType(item) === 1);
}

export function isWalletOnlyEoaTradeAddresses(records) {
  if (!Array.isArray(records) || !records.length) return false;
  if (hasCustodialTradeAddress(records)) return false;
  return hasSelfCustodyTradeAddress(records);
}

export function resolveRedeemExecutionMode(input = {}) {
  const sessionExpectsCustodial = Boolean(input.sessionExpectsCustodial);
  const hasCustodial = Boolean(input.hasCustodial);
  const hasSelfCustody = Boolean(input.hasSelfCustody);
  const connected = normalizeAddress(input.connectedWalletAddress);
  if (sessionExpectsCustodial) {
    return hasCustodial ? 'custodial' : 'custodial_missing';
  }
  if (hasSelfCustody || connected) return 'eoa';
  return 'none';
}

export function pickTradeAddressRecord(records) {
  if (!Array.isArray(records) || !records.length) return null;
  const custodial = records.find((item) => getTradeAddressType(item) === 2);
  return custodial ?? records[0];
}

export function resolvePrivySignerAddress(embeddedAddress, records = []) {
  const embedded = normalizeAddress(embeddedAddress);
  if (!embedded) return '';
  if (Array.isArray(records) && records.length) {
    const matched = records.find((item) => addressesEqual(item?.address, embedded));
    if (matched) return normalizeAddress(matched.address);
  }
  return embedded;
}

export function pickWalletFromTradeAddressList(records) {
  const picked = pickTradeAddressRecord(records);
  return String(picked?.address ?? '').trim();
}

export function isPrivyTradeAddressInList(records, address, privyWalletId) {
  const normalizedAddress = normalizeAddress(address);
  const normalizedWalletId = normalizeAddress(privyWalletId);
  if (!normalizedAddress || !normalizedWalletId || !Array.isArray(records)) return false;
  return records.some(
    (item) =>
      addressesEqual(item?.address, normalizedAddress) &&
      normalizeAddress(item?.privyWalletId) === normalizedWalletId &&
      (getTradeAddressType(item) === 2 || Boolean(normalizeAddress(item?.privyWalletId)))
  );
}

export function isTradeAddressDuplicateError(error) {
  const msg = String(error?.message ?? error ?? '').toLowerCase();
  return (
    msg.includes('already exist') ||
    msg.includes('already registered') ||
    msg.includes('duplicate') ||
    msg.includes('已存在') ||
    msg.includes('重复')
  );
}
